import React from 'react';
import TableHeader from '../BaseComponents/TableHeader';
import TrackerRow from './TrackerRow';
import './BloodUploadTrackerTable.scss';
import { getBloodUploadTracker, BloodUploadTrackerResult } from '../../api/oldBloodTest';
import { toast } from 'react-toastify';

const headerColumns = [
    { title: 'Name', key: 'memberName', minWidth: '200px', width: '200px', sortable: true, apiKey: 'sort_by_name' },
    { title: 'Plan Type', key: 'planType', minWidth: '100px', width: '100px', flex: 1 },
    { title: 'File Name', key: 'fileName', minWidth: '350px', width: '350px', flex: 1 },
    { title: 'Date uploaded', key: 'dateUploaded', minWidth: '150px', width: '150px', flex: 1 },
    { title: 'Status', key: 'status', minWidth: '150px', width: '150px', flex: 1 },
    { title: 'View', key: 'actions', minWidth: '60px', width: '60px', flex: 1 }
];

const PAGE_SIZE = 10;

interface BloodUploadTrackerTableProps {
    onCountUpdate: (count: number) => void;
    selectedPlanType?: string;
    startDate?: string;
    endDate?: string;
}

const BloodUploadTrackerTable: React.FC<BloodUploadTrackerTableProps> = ({ onCountUpdate, selectedPlanType, startDate, endDate }) => {
    const [data, setData] = React.useState<BloodUploadTrackerResult[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [hasMore, setHasMore] = React.useState<boolean>(true);
    const [sortByName, setSortByName] = React.useState<boolean>(false);
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');
    const offsetRef = React.useRef<number>(0);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const fetchData = React.useCallback(async (isInitial: boolean = false) => {
        if (loading || (!hasMore && !isInitial)) return;

        setLoading(true);
        try {
            const currentOffset = isInitial ? 0 : offsetRef.current;
            const response = await getBloodUploadTracker(
                PAGE_SIZE,
                currentOffset,
                '',
                selectedPlanType,
                sortByName,
                sortOrder,
                startDate,
                endDate
            );

            if (response.results) {
                if (isInitial) {
                    setData(response.results);
                    offsetRef.current = response.results.length;
                } else {
                    setData((prev) => [...prev, ...response.results]);
                    offsetRef.current = currentOffset + response.results.length;
                }

                onCountUpdate(response.total_count || 0);
                setHasMore(offsetRef.current < response.total_count);
            } else {
                setHasMore(false);
            }
        } catch (error: any) {
            console.error('Error fetching blood upload tracker data:', error);
            toast.error(error.message || 'Failed to fetch data');
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [loading, hasMore, onCountUpdate, selectedPlanType, sortByName, sortOrder, startDate, endDate]);

    const handleSort = React.useCallback((key: string, direction: 'asc' | 'desc') => {
        if (key !== 'sort_by_name') return;
        setSortByName(true);
        setSortOrder(direction);
    }, []);

    const handleScroll = React.useCallback(() => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            if (!scrollContainerRef.current || loading || !hasMore) return;

            const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
            const threshold = 100;
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
            const isContentSmallerThanContainer = scrollHeight <= clientHeight + threshold;

            if (isNearBottom || isContentSmallerThanContainer) {
                fetchData();
            }
        }, 100);
    }, [loading, hasMore, fetchData]);

    const fetchDataRef = React.useRef(fetchData);
    fetchDataRef.current = fetchData;

    React.useEffect(() => {
        setData([]);
        setHasMore(true);
        offsetRef.current = 0;
        fetchDataRef.current(true);
    }, [selectedPlanType, sortByName, sortOrder, startDate, endDate]);

    React.useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
            if (hasMore && data.length > 0) {
                setTimeout(() => handleScroll(), 100);
            }
        }
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [handleScroll, hasMore, data.length]);

    return (
        <div className='scroll-container'>
            <div className='table-container blood-upload-tracker-table mt-0'>
                <TableHeader headerColumns={headerColumns} gap={'24px'} onSort={handleSort} />
                <div className='table-body' ref={scrollContainerRef}>
                    {data.length === 0 && !loading ? (
                        <div className='empty-data' style={{ padding: '20px', textAlign: 'center' }}>
                            No entries found.
                        </div>
                    ) : (
                        data.map((item) => (
                            <TrackerRow key={item.id} rowData={item} />
                        ))
                    )}
                    {loading && <div style={{ padding: '10px', textAlign: 'center' }}>Loading...</div>}
                </div>
            </div>
        </div>
    );
};

export default BloodUploadTrackerTable;
